// -----------------------------------------------------------
// FloraIQ - RRR Methodological (Aba 6: RRR Metodológico)
// -----------------------------------------------------------

import { type FC, useMemo, useState } from "react";
import { ladmRrr } from "@/mocks/ladm";
import LadmStatusBadge from "./LadmStatusBadge";
import type { LadmBadgeVariant } from "./LadmStatusBadge";

const rrrStatusMap: Record<string, LadmBadgeVariant> = {
  vigente: "vigente",
  executada: "executada",
  pendente: "pendente",
  em_andamento: "em_andamento",
  em_analise: "em_analise",
};

const colorByTipo: Record<string, string> = {
  Responsabilidade: "bg-blue-50 text-blue-700",
  Restrição: "bg-orange-50 text-orange-700",
  Autorização: "bg-green-50 text-green-700",
};

const RrrMethodologicalSection: FC = () => {
  const [filtroTipo, setFiltroTipo] = useState("");

  const filtered = useMemo(() => {
    if (!filtroTipo) return ladmRrr;
    return ladmRrr.filter((r) => r.tipo === filtroTipo);
  }, [filtroTipo]);

  const counts = useMemo(() => {
    return {
      responsabilidade: ladmRrr.filter((r) => r.tipo === "Responsabilidade").length,
      restricao: ladmRrr.filter((r) => r.tipo === "Restrição").length,
      autorizacao: ladmRrr.filter((r) => r.tipo === "Autorização").length,
    };
  }, []);

  return (
    <div className="space-y-6">
      {/* Resumo */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white rounded-card border border-background-200/70 p-4 text-center">
          <p className="text-2xl font-bold text-blue-600 font-heading">{counts.responsabilidade}</p>
          <p className="text-xs text-foreground-500 mt-1">Responsabilidades</p>
        </div>
        <div className="bg-white rounded-card border border-background-200/70 p-4 text-center">
          <p className="text-2xl font-bold text-orange-600 font-heading">{counts.restricao}</p>
          <p className="text-xs text-foreground-500 mt-1">Restrições</p>
        </div>
        <div className="bg-white rounded-card border border-background-200/70 p-4 text-center">
          <p className="text-2xl font-bold text-green-600 font-heading">{counts.autorizacao}</p>
          <p className="text-xs text-foreground-500 mt-1">Autorizações</p>
        </div>
      </div>

      {/* Tabela */}
      <div className="bg-white rounded-card border border-background-200/70 overflow-hidden">
        <div className="flex flex-wrap items-center gap-3 p-4 border-b border-background-200/50">
          <span className="text-xs font-medium text-foreground-600">Filtro:</span>
          <select
            value={filtroTipo}
            onChange={(e) => setFiltroTipo(e.target.value)}
            className="text-xs px-2.5 py-1.5 rounded-md border border-background-200/70 bg-white text-foreground-700 cursor-pointer"
          >
            <option value="">Todos os tipos</option>
            <option value="Responsabilidade">Responsabilidade</option>
            <option value="Restrição">Restrição</option>
            <option value="Autorização">Autorização</option>
          </select>
          <span className="text-xs text-foreground-400 ml-auto">{filtered.length} registros</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-background-100/70">
                <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-600 font-heading whitespace-nowrap">Tipo RRR</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-600 font-heading whitespace-nowrap">Categoria</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-600 font-heading whitespace-nowrap">Unidade Vinculada</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-600 font-heading whitespace-nowrap">Parte Responsável</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-600 font-heading whitespace-nowrap">Documento Fonte</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-600 font-heading whitespace-nowrap">Prazo</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-foreground-600 font-heading whitespace-nowrap">Status</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-foreground-600 font-heading whitespace-nowrap">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-background-200/50">
              {filtered.map((rrr) => (
                <tr key={rrr.id} className="hover:bg-background-50/60 transition-colors">
                  <td className="px-4 py-2.5">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${colorByTipo[rrr.tipo] || "bg-gray-50 text-gray-700"}`}>
                      {rrr.tipo}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-foreground-700">{rrr.categoria}</td>
                  <td className="px-4 py-2.5 text-xs text-foreground-700 font-mono">{rrr.unidadeVinculada}</td>
                  <td className="px-4 py-2.5 text-xs text-foreground-700">{rrr.parteResponsavel}</td>
                  <td className="px-4 py-2.5 text-xs text-foreground-600 font-mono">{rrr.documentoFonte}</td>
                  <td className="px-4 py-2.5 text-xs text-foreground-500 whitespace-nowrap">{rrr.prazoValidade}</td>
                  <td className="px-4 py-2.5 text-center">
                    <LadmStatusBadge variant={rrrStatusMap[rrr.status] || "vigente"} />
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    <button className="text-xs text-blue-600 hover:text-blue-700 cursor-pointer whitespace-nowrap">
                      <i className="ri-eye-line mr-1"></i>Ver
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default RrrMethodologicalSection;